import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Types
interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePicture: string | null;
}

interface FieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

interface ActivityLogEntry {
  id: string;
  entityType: 'task' | 'comment' | 'dependency' | 'assignment';
  entityId: string;
  action: 'create' | 'update' | 'delete';
  userId: string;
  changes: FieldChange[];
  user?: User;
  createdAt: string;
}

type ChangeFilter = 'all' | 'field' | 'dependency' | 'assignment' | 'status';

// Query keys
export const historyKeys = {
  all: ['history'] as const,
  lists: () => [...historyKeys.all, 'list'] as const,
  byTask: (taskId: string, filter?: ChangeFilter) =>
    [...historyKeys.lists(), 'task', taskId, filter] as const,
};

// Fetch history for a task
export function useHistoryQuery(taskId: string, filter?: ChangeFilter) {
  return useQuery({
    queryKey: historyKeys.byTask(taskId, filter),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter && filter !== 'all') {
        params.append('filter', filter);
      }
      const queryString = params.toString();
      const url = `/tasks/${taskId}/history${queryString ? `?${queryString}` : ''}`;
      const response = await api.get<ActivityLogEntry[]>(url);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 30000,
  });
}

export type { ActivityLogEntry, FieldChange, ChangeFilter };
